import React from 'react'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
} from '@chakra-ui/react'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import xhr from 'xhr'
import { BRAND } from '../../../components/ui'

const schema = Yup.object().shape({
  first_name: Yup.string().trim().required('Escribe los nombres.'),
  last_name: Yup.string().trim().required('Escribe los apellidos.'),
  email: Yup.string().trim().email('El correo no es válido.').required('Escribe el correo.'),
  cellphone: Yup.string()
    .trim()
    .required('Escribe el celular.')
    .test('digits', 'El celular debe tener al menos 7 dígitos.', (value) => (value || '').replace(/\D/g, '').length >= 7),
  country: Yup.string().required('Selecciona el país.'),
})

// El formulario siempre abre vacío; solo el país viene preseleccionado
// (el del prospecto o Colombia).
const initialValues = (prospect, countries) => {
  const colombia = countries.find((el) => el.code === 'CO' || String(el.phone_code) === '57')
  const country = prospect?.country?.id || colombia?.id || ''
  return { first_name: '', last_name: '', email: '', cellphone: '', country: country ? String(country) : '' }
}

export default function RegisterStudentModal({ isOpen, onClose, prospect, countries, onCreated, onLinkExisting }) {
  const [conflict, setConflict] = React.useState(null)
  const [error, setError] = React.useState('')

  const formValues = React.useMemo(() => initialValues(prospect, countries), [prospect, countries])

  const formik = useFormik({
    initialValues: formValues,
    enableReinitialize: true,
    validationSchema: schema,
    onSubmit: async (values, helpers) => {
      setConflict(null)
      setError('')
      try {
        const res = await xhr.post('/advisers/students', { ...values, prospect: prospect.id })
        onCreated(res.data)
        helpers.resetForm()
      } catch (err) {
        const response = err?.response
        const apiError = response?.data?.error
        if (response?.status === 409) {
          setConflict(apiError?.details || {})
        } else {
          setError(apiError?.message || 'No se pudo registrar el estudiante. Inténtalo de nuevo.')
        }
      } finally {
        helpers.setSubmitting(false)
      }
    },
  })

  const handleClose = () => {
    setConflict(null)
    setError('')
    formik.resetForm()
    onClose()
  }

  const fieldProps = (name) => ({
    id: `register-${name}`,
    name,
    value: formik.values[name],
    onChange: formik.handleChange,
    onBlur: formik.handleBlur,
  })
  const invalid = (name) => Boolean(formik.touched[name] && formik.errors[name])

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalOverlay />
      <ModalContent as="form" onSubmit={formik.handleSubmit} noValidate>
        <ModalHeader fontSize="lg">Registrar estudiante</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm" color="gray.600" mb={4}>
            Se crea la cuenta en Klazia, queda a tu cargo y vinculada a este prospecto. Al guardar verás la contraseña
            temporal para compartirla.
          </Text>

          {conflict && (
            <Alert status="warning" borderRadius="md" mb={4} alignItems="flex-start">
              <AlertIcon />
              <Box fontSize="sm">
                <AlertDescription>
                  Ya existe una cuenta con ese {conflict.matchedBy === 'email' ? 'correo' : 'celular'}
                  {conflict.student ? `: ${conflict.student.name} (código ${conflict.student.id}).` : '.'}
                </AlertDescription>
                {conflict.student && (
                  <Button
                    mt={2}
                    size="sm"
                    variant="outline"
                    colorScheme="purple"
                    onClick={() => onLinkExisting(conflict.student.id)}
                  >
                    Vincular esa cuenta a este prospecto
                  </Button>
                )}
              </Box>
            </Alert>
          )}

          {error && (
            <Alert status="error" borderRadius="md" mb={4} fontSize="sm">
              <AlertIcon />
              {error}
            </Alert>
          )}

          <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
            <FormControl isRequired isInvalid={invalid('first_name')}>
              <FormLabel fontSize="sm">Nombres</FormLabel>
              <Input {...fieldProps('first_name')} />
              <FormErrorMessage>{formik.errors.first_name}</FormErrorMessage>
            </FormControl>
            <FormControl isRequired isInvalid={invalid('last_name')}>
              <FormLabel fontSize="sm">Apellidos</FormLabel>
              <Input {...fieldProps('last_name')} />
              <FormErrorMessage>{formik.errors.last_name}</FormErrorMessage>
            </FormControl>
            <FormControl isRequired isInvalid={invalid('email')} gridColumn={{ md: 'span 2' }}>
              <FormLabel fontSize="sm">Correo del estudiante</FormLabel>
              <Input type="email" {...fieldProps('email')} />
              <FormErrorMessage>{formik.errors.email}</FormErrorMessage>
            </FormControl>
            <FormControl isRequired isInvalid={invalid('country')}>
              <FormLabel fontSize="sm">País</FormLabel>
              <Select {...fieldProps('country')} placeholder="Selecciona">
                {countries.map((el) => (
                  <option key={el.id} value={el.id}>
                    {el.name} {el.phone_code ? `+${el.phone_code}` : ''}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{formik.errors.country}</FormErrorMessage>
            </FormControl>
            <FormControl isRequired isInvalid={invalid('cellphone')}>
              <FormLabel fontSize="sm">Celular del estudiante</FormLabel>
              <Input type="tel" {...fieldProps('cellphone')} />
              <FormErrorMessage>{formik.errors.cellphone}</FormErrorMessage>
            </FormControl>
          </Grid>
          <Text fontSize="xs" color="gray.500" mt={3}>
            El correo y el celular no pueden repetirse entre cuentas de Klazia.
          </Text>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            type="submit"
            bg={BRAND}
            color="white"
            _hover={{ bg: '#463fd1' }}
            isLoading={formik.isSubmitting}
            loadingText="Creando cuenta"
          >
            Crear cuenta
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
